// AgencySubscriptionView — agency owner billing view (Pricing v3.2).
//
// v3.2 (Fase 4B 21/05 20:00) — REFACTOR sostanziale:
//  - Dropped PriceCalculator (modello per-recruiter con volume discount)
//  - Dropped TIERS + computePricing + nextTierFor + Row helpers
//  - Dropped AGENCY_LIMITS interno + AgencyCompareTable interna
//  - Dropped ToggleGroup monthly/annual (annual cycle dropped in v3.2)
//
// Nuovo flow (allineato a AgencyDiscoverySection):
//  - Header: "X aziende attive · €Y/mese" con plan corrente
//  - 3 card tier (Starter / Medium / Professional) con stile bullet
//    sintetico v3.2 + bottoni "Upgrade" o "Piano corrente"
//  - Click su tier diverso → apre AgencyChooserDialog stepper per pre-
//    acquistare il numero di seats per il nuovo tier
//  - Card Enterprise sales-led (mailto info@hiromatch.com)
//  - Compare table sotto le card (riusa stessa logica AgencyDiscoverySection)
//
// L'agency owner usa questa view per:
//  - vedere lo stato corrente (piano + companies attive + totale mensile)
//  - cambiare tier (upgrade Starter→Medium/Pro o downgrade)
//  - eventualmente aggiungere companies seats (chooser stepper)
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ScheduledPlanChangeBanner } from '@/components/billing/ScheduledPlanChangeBanner';
import { Check, Loader2, Star, Crown, Minus, Plus, X, Infinity as InfinityIcon, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/services/api';
import { ApiError } from '@/services/httpClient';
import { emitPlanChanged } from '@/lib/billingEvents';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import type { AgencyContext } from '@/types';

type AgencyPlanId = 'agency_starter' | 'agency_medium' | 'agency_professional';

const AGENCY_TIER_BANDS: Record<AgencyPlanId, { min: number; max: number; priceMonthly: number }> = {
  agency_starter: { min: 1, max: 5, priceMonthly: 99 },
  agency_medium: { min: 6, max: 10, priceMonthly: 249 },
  agency_professional: { min: 11, max: 20, priceMonthly: 449 },
};

/**
 * v3.2 polish (21/05/26) — per-company effective caps that an agency tier
 * inherits from its equivalent SME tier. Same values as the rows in
 * `AgencyCompareTable` below; extracted here so the summary card in the
 * header can render the 4 key numbers of the current plan without a BE
 * round-trip.
 */
// v3.5 (25/05/26 — Split Pools):
// - candidates invariati (300/1500/5000) — gate CV screening
// - aiCredits ridotti a 150/500/1500 (era 150/1000/5000) — pool unico
//   ai_company per chat/transcript/autofill/interview-report
const AGENCY_PER_COMPANY_LIMITS: Record<AgencyPlanId, {
  jobs: number; candidates: number; seats: number; aiCredits: number;
}> = {
  agency_starter: { jobs: 5, candidates: 300, seats: 3, aiCredits: 150 },
  agency_medium: { jobs: 10, candidates: 1500, seats: 10, aiCredits: 500 },
  agency_professional: { jobs: 50, candidates: 5000, seats: 25, aiCredits: 1500 },
};

const ENTERPRISE_CONTACT_EMAIL = 'info@hiromatch.com';

interface AgencyFeature {
  label: string;
  comingSoon?: boolean;
}

function normalizeCheckoutRedirectUrl(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  let t = input.trim();
  if (!t) return null;
  if (t.startsWith('//')) t = `${window.location.protocol}${t}`;
  try {
    const u = new URL(t);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.toString();
  } catch { return null; }
}

export function AgencySubscriptionView({ agency }: { agency: AgencyContext }) {
  const { t, language } = useLanguage();
  const isIt = language === 'it';
  const { refreshSession } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // v3.2: ownedCompanies arriva dal BE come `agency.recruitersCount` (legacy
  // field name — vedi user.controller.ts response shape). In Fase 5 cleanup
  // FE potremmo accedere a `agency.ownedCompanies`. Per ora usiamo il legacy.
  const ownedCompanies = agency.recruitersCount ?? agency.clients?.length ?? 1;
  const currentPlanId = agency.agencyPlanId as AgencyPlanId;
  const currentBand = AGENCY_TIER_BANDS[currentPlanId];
  const currentTotalMonthly = (currentBand?.priceMonthly ?? 0) * Math.max(ownedCompanies, 1);

  // v3.2 polish (22/05/26 ~18:30): scheduled tier change banner.
  // The agency owner pays in advance for the current cycle, so when they
  // click Downgrade Stripe schedules the change to the end of period —
  // current product stays `active` (with `canceled_at` stamped), and a
  // new product appears with `status=scheduled` for the target tier.
  // Without surfacing this, the user sees "Medium attivo" and assumes
  // their downgrade click failed. The BE exposes the pair on
  // `/companies/:cid/billing/status`.
  const { data: billingStatus } = useBillingStatus();
  const scheduled = billingStatus?.scheduled;
  const cancelAtPeriodEnd = !scheduled ? billingStatus?.cancelAtPeriodEnd : undefined;

  const [chooserPlan, setChooserPlan] = useState<AgencyPlanId | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // v3.5.2 polish (26/05/2026): Stripe Customer Portal CTA (era già in
  // SubscriptionTab SME, mancava nella view Agency owner).
  const [portalLoading, setPortalLoading] = useState(false);

  // ── Checkout success handler ──────────────────────────────────────────
  // v3.5.2 polish (26/05/26): polling con retry per gestire la race
  // Stripe → Autumn → BE webhook (vedi SubscriptionTab.tsx per dettagli).
  const checkoutFlag = searchParams.get('checkout');
  const checkoutPlan = searchParams.get('plan');
  useEffect(() => {
    if (checkoutFlag !== 'success') return;
    const cid = agency.clients[0]?.companyId;
    if (!cid) return;
    const targetPlan = checkoutPlan;
    void (async () => {
      try {
        const POLL_INTERVAL_MS = 2000;
        const POLL_MAX_ATTEMPTS = 10; // 20s totali
        let confirmed = false;
        for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
          await api.billing.reconcile(cid);
          await queryClient.invalidateQueries({ queryKey: ['billing'] });
          try {
            const status = await api.billing.getStatus(cid);
            if (!targetPlan || status?.planId === targetPlan) {
              confirmed = true;
              break;
            }
          } catch {
            // ignore, ritento
          }
          if (attempt < POLL_MAX_ATTEMPTS - 1) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          }
        }
        await refreshSession();
        emitPlanChanged({ source: 'agency-checkout', planId: targetPlan ?? undefined });
        await queryClient.invalidateQueries({ queryKey: ['billing'] });
        if (confirmed) {
          toast.success(t('subscription.checkout.success_toast', { plan: targetPlan ?? '—' }));
        } else {
          toast.success(t('subscription.checkout.applied'), {
            description: t('subscription.checkout.success_toast', { plan: targetPlan ?? '—' }),
          });
        }
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : (e as Error).message;
        toast.error(t('subscription.checkout.error'), { description: msg });
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('checkout');
        next.delete('plan');
        setSearchParams(next, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutFlag]);

  // ── After-portal reconcile ─────────────────────────────────────────────
  // v3.5.2 polish (26/05/26): stesso handler di SubscriptionTab SME — quando
  // l'utente torna dal Stripe Customer Portal con `?fromPortal=1`, ricarica
  // billing + session + emette plan-changed evento.
  const fromPortalFlag = searchParams.get('fromPortal');
  useEffect(() => {
    if (fromPortalFlag !== '1') return;
    const cid = agency.clients[0]?.companyId;
    if (!cid) return;
    void (async () => {
      try {
        await api.billing.reconcile(cid);
        await queryClient.invalidateQueries({ queryKey: ['billing'] });
        emitPlanChanged({ source: 'agency-portal-return' });
        await refreshSession();
      } catch { /* ignore */ }
      const next = new URLSearchParams(searchParams);
      next.delete('fromPortal');
      setSearchParams(next, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromPortalFlag]);

  const portalReturnUrl = () => {
    const u = new URL(window.location.href);
    u.searchParams.set('tab', 'billing');
    u.searchParams.set('fromPortal', '1');
    u.searchParams.delete('checkout');
    return u.toString();
  };

  const openPortal = async () => {
    const cid = agency.clients[0]?.companyId;
    if (!cid) {
      toast.error(t('subscription.checkout.error'));
      return;
    }
    setPortalLoading(true);
    try {
      const { url } = await api.billing.getPortalUrl(cid, portalReturnUrl());
      window.location.href = url;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      toast.error(t('subscription.checkout.error'), { description: msg });
    } finally {
      setPortalLoading(false);
    }
  };

  const handleConfirmChooser = async (quantity: number) => {
    if (!chooserPlan) return;
    const cid = agency.clients[0]?.companyId;
    if (!cid) {
      toast.error(t('subscription.checkout.error'));
      return;
    }
    setSubmitting(true);
    try {
      const successUrl = (() => {
        const u = new URL(window.location.href);
        u.searchParams.set('tab', 'billing');
        u.searchParams.set('checkout', 'success');
        u.searchParams.set('plan', chooserPlan);
        return u.toString();
      })();
      const raw = await api.billing.checkout(cid, {
        planId: chooserPlan,
        billingPeriod: 'monthly',
        desiredCompanies: quantity,
        successUrl,
      });
      const payload = raw as { checkoutUrl?: string | null; checkout_url?: string | null };
      const href = normalizeCheckoutRedirectUrl(payload.checkoutUrl ?? payload.checkout_url);
      if (href) {
        window.location.assign(href);
        return;
      }
      toast.success(t('subscription.checkout.applied'));
      await Promise.all([
        refreshSession(),
        queryClient.invalidateQueries({ queryKey: ['billing'] }),
      ]);
      setChooserPlan(null);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      toast.error(t('subscription.checkout.error'), { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const featuresFor = (id: AgencyPlanId): AgencyFeature[] => {
    if (id === 'agency_starter') {
      return isIt
        ? [
            { label: '1–5 aziende clienti' },
            { label: '5 job / azienda' },
            { label: '300 candidati AI / azienda / mese' },
            { label: '3 utenti / azienda' },
            { label: '150 AI Credits / azienda / mese' },
            { label: 'Email prioritaria' },
            { label: 'DB candidati centralizzato', comingSoon: true },
          ]
        : [
            { label: '1–5 client companies' },
            { label: '5 jobs / company' },
            { label: '300 AI candidates / company / mo' },
            { label: '3 seats / company' },
            { label: '150 AI Credits / company / mo' },
            { label: 'Priority email support' },
            { label: 'Centralized candidate DB', comingSoon: true },
          ];
    }
    if (id === 'agency_medium') {
      return isIt
        ? [
            { label: '6–10 aziende clienti' },
            { label: '10 job / azienda' },
            { label: '1.500 candidati AI / azienda / mese' },
            { label: '10 utenti / azienda' },
            // v3.5: 1.000 → 500 AI Credits per company
            { label: '500 AI Credits / azienda / mese' },
            { label: 'Chat dedicata' },
            { label: 'Matching AI cross-client', comingSoon: true },
          ]
        : [
            { label: '6–10 client companies' },
            { label: '10 jobs / company' },
            { label: '1,500 AI candidates / company / mo' },
            { label: '10 seats / company' },
            { label: '500 AI Credits / company / mo' },
            { label: 'Dedicated chat' },
            { label: 'Cross-client AI matching', comingSoon: true },
          ];
    }
    return isIt
      ? [
          { label: '11–20 aziende clienti' },
          { label: '50 job / azienda' },
          { label: '5.000 candidati AI / azienda / mese' },
          { label: '25 utenti / azienda' },
          // v3.5: 5.000 → 1.500 AI Credits per company
          { label: '1.500 AI Credits / azienda / mese' },
          { label: 'Account manager dedicato', comingSoon: true },
        ]
      : [
          { label: '11–20 client companies' },
          { label: '50 jobs / company' },
          { label: '5,000 AI candidates / company / mo' },
          { label: '25 seats / company' },
          { label: '1,500 AI Credits / company / mo' },
          { label: 'Dedicated account manager', comingSoon: true },
        ];
  };

  const titleFor = (id: AgencyPlanId) => t(`subscription.tier.${id}`);

  const chooserBand = chooserPlan ? AGENCY_TIER_BANDS[chooserPlan] : null;

  const currentLimits = AGENCY_PER_COMPANY_LIMITS[currentPlanId];
  const nfmt = (n: number) => n.toLocaleString(isIt ? 'it-IT' : 'en-US');

  return (
    <div className="space-y-8">
      <ScheduledPlanChangeBanner
        scheduled={scheduled}
        cancelAtPeriodEnd={cancelAtPeriodEnd}
        currentPlanLabel={titleFor(currentPlanId)}
        owner={billingStatus?.owner}
        language={language}
      />

      {/* Header — current plan summary */}
      <div className="rounded-lg border border-brand-primary/30 bg-brand-primary/5 px-5 py-4 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Crown className="h-5 w-5 text-brand-primary" />
              {t('subscription.agency.title')}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t('subscription.agency.currentPlan')}:{' '}
              <span className="font-medium text-foreground">{titleFor(currentPlanId)}</span>
              <span className="text-muted-foreground">
                {' '}· {ownedCompanies} {isIt ? 'aziende attive' : 'active companies'}
                {' '}· €{currentTotalMonthly}/{isIt ? 'mese' : 'mo'}
              </span>
            </p>
          </div>
          {/* v3.5.2 polish (26/05/26): "Gestisci su Stripe" CTA — era presente
              in SubscriptionTab SME ma mancava qui per agency owner. */}
          <Button
            variant="outline"
            size="sm"
            onClick={openPortal}
            disabled={portalLoading || submitting}
            aria-label={t('subscription.portal.cta')}
          >
            {portalLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
            {t('subscription.portal.cta')}
          </Button>
        </div>
        {currentLimits && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-brand-primary/10 rounded-md overflow-hidden border border-brand-primary/10">
            <PlanSummaryCell
              value={`${nfmt(currentLimits.jobs)} ${t('subscription.agency.summary.jobs').replace('{n}', '').trim()}`}
              label={t('subscription.agency.summary.perCompany')}
            />
            <PlanSummaryCell
              value={`${nfmt(currentLimits.candidates)} ${t('subscription.agency.summary.candidates').replace('{n}', '').trim()}`}
              label={t('subscription.agency.summary.perCompanyMonth')}
            />
            <PlanSummaryCell
              value={`${nfmt(currentLimits.seats)} ${t('subscription.agency.summary.seats').replace('{n}', '').trim()}`}
              label={t('subscription.agency.summary.perCompany')}
            />
            <PlanSummaryCell
              value={`${nfmt(currentLimits.aiCredits)} ${t('subscription.agency.summary.aiCredits').replace('{n}', '').trim()}`}
              label={t('subscription.agency.summary.perCompanyMonth')}
            />
          </div>
        )}
      </div>

      {/* Plan cards — Starter / Medium / Professional */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {(['agency_starter', 'agency_medium', 'agency_professional'] as AgencyPlanId[]).map(id => {
          const isCurrent = id === currentPlanId;
          const features = featuresFor(id);
          const band = AGENCY_TIER_BANDS[id];

          // Sprint 2 follow-up (22/05/26 ~18:15) — directional CTA labels.
          // Pre-fix: every non-current card said "Procedi al pagamento",
          // regardless of whether the user was upgrading (Medium → Pro)
          // or downgrading (Medium → Starter). UX confusion: a Medium
          // owner clicking Starter believed they were signing up for
          // something new instead of stepping down their subscription.
          //
          // Rank order: starter=1, medium=2, professional=3. We compare
          // against the current tier rank to pick the right verb.
          const AGENCY_TIER_RANK: Record<AgencyPlanId, number> = {
            agency_starter: 1,
            agency_medium: 2,
            agency_professional: 3,
            agency_enterprise: 4, // not rendered in this map, kept for completeness
          };
          const targetRank = AGENCY_TIER_RANK[id];
          const currentRank = AGENCY_TIER_RANK[currentPlanId];
          const direction: 'upgrade' | 'downgrade' | 'current' =
            isCurrent ? 'current' : targetRank > currentRank ? 'upgrade' : 'downgrade';
          const isThisLoading = submitting && chooserPlan === id;
          const ctaLabel = direction === 'current'
            ? t('subscription.agency.cta.current')
            : direction === 'upgrade'
              ? t('subscription.agency.cta.upgrade')
              : t('subscription.agency.cta.downgrade');
          return (
            <Card
              key={id}
              className={cn(
                'relative p-6 flex flex-col gap-4',
                isCurrent && 'border-brand-primary ring-1 ring-brand-primary',
              )}
            >
              {isCurrent && (
                <Badge className="absolute -top-2 left-4 bg-brand-primary text-white gap-1">
                  <Star className="h-3 w-3 fill-current" /> {t('subscription.agency.cta.current').toUpperCase()}
                </Badge>
              )}
              <div>
                <h3 className="font-display font-semibold text-lg">{titleFor(id)}</h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold">€{band.priceMonthly}</span>
                  <span className="text-sm text-muted-foreground">
                    {' '}{isIt ? '/azienda/mese' : '/company/mo'}
                  </span>
                </div>
              </div>
              <ul className="space-y-2 flex-1">
                {features.map(f => (
                  <li
                    key={f.label}
                    className={cn(
                      'flex items-start gap-2 text-sm',
                      f.comingSoon && 'text-muted-foreground',
                    )}
                  >
                    <Check
                      className={cn(
                        'h-4 w-4 mt-0.5 flex-shrink-0',
                        f.comingSoon ? 'text-muted-foreground/60' : 'text-brand-primary',
                      )}
                    />
                    <span className="flex-1">
                      {f.label}
                      {f.comingSoon && (
                        <>
                          {' '}
                          <Badge
                            variant="outline"
                            className="ml-1 text-[10px] px-1.5 py-0 font-normal border-muted-foreground/30 text-muted-foreground bg-muted/40"
                            title={t('subscription.feature.q3_2026_tooltip')}
                          >
                            {t('subscription.feature.comingSoon')}
                          </Badge>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <Button
                className={cn(
                  'w-full',
                  direction === 'upgrade' && 'bg-brand-primary hover:bg-brand-primary-mid text-white',
                  // Downgrade: keep the visual distinct (outline) so it doesn't
                  // look like a primary action — even if the click flow is the
                  // same (chooser stepper → checkout).
                  direction === 'downgrade' && 'border-warning text-warning hover:bg-warning/10',
                )}
                variant={direction === 'upgrade' ? 'default' : 'outline'}
                disabled={isCurrent || submitting || chooserPlan !== null}
                onClick={() => setChooserPlan(id)}
                aria-busy={isThisLoading}
              >
                {isThisLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isThisLoading ? t('subscription.checkout.processing') : ctaLabel}
              </Button>
            </Card>
          );
        })}

        {/* Enterprise sales-led */}
        <EnterpriseTierCard isIt={isIt} />
      </div>

      {/* Compare table — same shape as AgencyDiscoverySection */}
      <AgencyCompareTable />

      {/* Chooser dialog — pre-buy company seats for the selected new tier */}
      <Dialog
        open={!!chooserPlan}
        onOpenChange={(v) => { if (!v && !submitting) setChooserPlan(null); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('subscription.agency.chooserTitle')}</DialogTitle>
            <DialogDescription>{t('subscription.agency.chooserSubtitle')}</DialogDescription>
          </DialogHeader>
          {chooserBand && chooserPlan && (
            <ChooserStepper
              title={titleFor(chooserPlan)}
              min={chooserBand.min}
              max={chooserBand.max}
              priceMonthly={chooserBand.priceMonthly}
              initialQuantity={Math.max(
                chooserBand.min,
                Math.min(chooserBand.max, ownedCompanies),
              )}
              submitting={submitting}
              onCancel={() => setChooserPlan(null)}
              onConfirm={handleConfirmChooser}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * PlanSummaryCell — single cell in the 2x2/4-col grid that summarises the
 * per-company caps of the current agency plan. Used in the header card.
 */
function PlanSummaryCell({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-brand-primary/5 px-3 py-2.5 text-center">
      <div className="text-base sm:text-lg font-semibold text-foreground leading-tight">
        {value}
      </div>
      <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 uppercase tracking-wide">
        {label}
      </div>
    </div>
  );
}

function ChooserStepper({
  title, min, max, priceMonthly, initialQuantity, submitting, onCancel, onConfirm,
}: {
  title: string;
  min: number;
  max: number;
  priceMonthly: number;
  initialQuantity: number;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (quantity: number) => void;
}) {
  const { t, language } = useLanguage();
  const isIt = language === 'it';
  const [quantity, setQuantity] = useState<number>(initialQuantity);

  const dec = () => setQuantity((q) => Math.max(min, q - 1));
  const inc = () => setQuantity((q) => Math.min(max, q + 1));

  const total = priceMonthly * quantity;

  return (
    <div className="space-y-5 py-2">
      <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
        <p className="font-medium text-foreground">{title}</p>
      </div>

      <div className="flex items-center justify-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={dec}
          disabled={submitting || quantity <= min}
          aria-label="decrement"
        >
          <Minus className="h-4 w-4" />
        </Button>
        <div className="min-w-[80px] text-center">
          <div className="text-4xl font-bold">{quantity}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {isIt ? 'aziende' : 'companies'}
          </div>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={inc}
          disabled={submitting || quantity >= max}
          aria-label="increment"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <div className="rounded-md border border-brand-primary/20 bg-brand-primary/5 p-4 text-center space-y-1">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">
          {t('subscription.agency.chooserTotalPreview', { total, n: quantity })}
        </p>
        <p className="text-3xl font-bold text-brand-primary">
          €{total}
          <span className="text-base font-normal text-muted-foreground">
            {' '}{isIt ? '/mese' : '/mo'}
          </span>
        </p>
        <p className="text-xs text-muted-foreground">
          {t('subscription.agency.pricePerCompany', { price: priceMonthly, n: quantity, total })}
        </p>
      </div>

      <DialogFooter className="gap-2 sm:gap-2">
        <Button variant="outline" onClick={onCancel} disabled={submitting}>
          {t('subscription.agency.chooserCancel')}
        </Button>
        <Button
          className="bg-brand-primary hover:bg-brand-primary-mid text-white"
          disabled={submitting}
          onClick={() => onConfirm(quantity)}
        >
          {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {submitting ? t('subscription.checkout.processing') : t('subscription.agency.chooserConfirm')}
        </Button>
      </DialogFooter>
    </div>
  );
}

/**
 * AgencyCompareTable — feature matrix v3.2 (mirrora AgencyDiscoverySection).
 * Kept in-file per evitare circular import; estraibile in shared in Fase 5
 * cleanup successivo.
 */
function AgencyCompareTable() {
  const { t } = useLanguage();

  type CellValue = 'check' | 'comingSoon' | 'crossInfinity' | string | number;
  interface Row {
    label: string;
    values: Record<string, CellValue>;
  }

  const rows: Row[] = useMemo(() => [
    {
      label: t('subscription.agency.compare.row.companies'),
      values: { agency_starter: '1–5', agency_medium: '6–10', agency_professional: '11–20' },
    },
    {
      label: t('subscription.agency.compare.row.pricePerCompany'),
      values: { agency_starter: '€99', agency_medium: '€249', agency_professional: '€449' },
    },
    {
      label: t('subscription.agency.compare.row.jobsPerCompany'),
      values: { agency_starter: 5, agency_medium: 10, agency_professional: 50 },
    },
    {
      label: t('subscription.agency.compare.row.candidatesPerCompany'),
      values: { agency_starter: 300, agency_medium: 1500, agency_professional: 5000 },
    },
    {
      label: t('subscription.agency.compare.row.seatsPerCompany'),
      values: { agency_starter: 3, agency_medium: 10, agency_professional: 25 },
    },
    {
      label: t('subscription.agency.compare.row.aiCreditsPerCompany'),
      // v3.5: caps ridotti (era 150/1000/5000)
      values: { agency_starter: 150, agency_medium: 500, agency_professional: 1500 },
    },
    {
      label: t('subscription.agency.compare.row.support'),
      values: {
        agency_starter: t('subscription.agency.support.email'),
        agency_medium: t('subscription.agency.support.chat'),
        agency_professional: t('subscription.agency.support.accountManager'),
      },
    },
    {
      label: t('subscription.agency.compare.row.centralizedDb'),
      values: { agency_starter: 'comingSoon', agency_medium: 'comingSoon', agency_professional: 'comingSoon' },
    },
    {
      label: t('subscription.agency.compare.row.crossClientAi'),
      values: { agency_starter: 'crossInfinity', agency_medium: 'comingSoon', agency_professional: 'comingSoon' },
    },
    {
      label: t('subscription.agency.compare.row.accountManager'),
      values: { agency_starter: 'crossInfinity', agency_medium: 'crossInfinity', agency_professional: 'comingSoon' },
    },
  ], [t]);

  const renderCell = (v: CellValue) => {
    if (v === 'check') return <Check className="h-4 w-4 text-brand-primary mx-auto" />;
    if (v === 'crossInfinity') return <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />;
    if (v === 'comingSoon') {
      return (
        <Badge
          variant="outline"
          className="text-[10px] px-1.5 py-0 font-normal border-muted-foreground/30 text-muted-foreground bg-muted/40"
          title={t('subscription.feature.q3_2026_tooltip')}
        >
          {t('subscription.feature.comingSoon')}
        </Badge>
      );
    }
    if (v === 'unlimited') return <InfinityIcon className="h-4 w-4 text-brand-primary mx-auto" />;
    if (typeof v === 'number') {
      return <span className="text-xs font-medium">{v.toLocaleString('it-IT')}</span>;
    }
    return <span className="text-xs font-medium">{v}</span>;
  };

  const tiers: AgencyPlanId[] = ['agency_starter', 'agency_medium', 'agency_professional'];

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">{t('subscription.agency.compare.title')}</h3>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="bg-muted/50 text-xs">
              <th className="text-left p-3 font-medium">{t('subscription.compare.feature')}</th>
              {tiers.map(p => (
                <th key={p} className="text-center p-3 font-medium">
                  {t(`subscription.tier.${p}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.label} className="border-t border-border">
                <td className="p-3 text-muted-foreground">{row.label}</td>
                {tiers.map(p => (
                  <td key={p} className="p-3 text-center">
                    {renderCell(row.values[p] ?? 'crossInfinity')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * EnterpriseTierCard — sales-led Enterprise card, stessa logica della
 * AgencyDiscoverySection (bullet sintetici + CTA "Contattaci" → info@).
 */
function EnterpriseTierCard({ isIt }: { isIt: boolean }) {
  const { t } = useLanguage();
  const subject = encodeURIComponent(t('agency.tier.enterprise.contactSubject'));
  const body = encodeURIComponent(t('agency.tier.enterprise.contactBody'));
  const mailto = `mailto:${ENTERPRISE_CONTACT_EMAIL}?subject=${subject}&body=${body}`;

  const features: AgencyFeature[] = isIt
    ? [
        { label: '20+ aziende clienti' },
        { label: 'Cap personalizzati per cliente' },
        { label: 'White-label completo (logo, dominio, email)' },
        { label: 'CSM dedicato', comingSoon: true },
        { label: 'Modelli AI custom', comingSoon: true },
      ]
    : [
        { label: '20+ client companies' },
        { label: 'Custom per-company caps' },
        { label: 'Full white-label (logo, domain, email)' },
        { label: 'Dedicated CSM', comingSoon: true },
        { label: 'Custom AI models', comingSoon: true },
      ];

  return (
    <Card className="relative p-6 flex flex-col gap-4 border-brand-accent/40 bg-gradient-to-br from-brand-accent/5 to-transparent">
      <Badge
        variant="outline"
        className="absolute -top-2 right-4 gap-1 border-brand-accent text-brand-accent bg-background"
      >
        <Crown className="h-3 w-3" />
        {t('agency.tier.enterprise.badge')}
      </Badge>
      <div>
        <h3 className="font-display font-semibold text-lg">
          {t('agency.tier.enterprise.title')}
        </h3>
      </div>
      <ul className="space-y-2 flex-1">
        {features.map(f => (
          <li
            key={f.label}
            className={cn(
              'flex items-start gap-2 text-sm',
              f.comingSoon && 'text-muted-foreground',
            )}
          >
            <Check
              className={cn(
                'h-4 w-4 mt-0.5 flex-shrink-0',
                f.comingSoon ? 'text-muted-foreground/60' : 'text-brand-accent',
              )}
            />
            <span className="flex-1">
              {f.label}
              {f.comingSoon && (
                <>
                  {' '}
                  <Badge
                    variant="outline"
                    className="ml-1 text-[10px] px-1.5 py-0 font-normal border-muted-foreground/30 text-muted-foreground bg-muted/40"
                    title={t('subscription.feature.q3_2026_tooltip')}
                  >
                    {t('subscription.feature.comingSoon')}
                  </Badge>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
      <Button
        asChild
        variant="outline"
        className="w-full border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-white"
      >
        <a href={mailto}>{t('subscription.agency.enterprise.contactCta')}</a>
      </Button>
    </Card>
  );
}
